/**
 * Avalia se uma condição é verdadeira baseado nos dados do formulário
 * Suporta todos os operadores do Form Builder
 */
export function evaluateCondition(condition, formData, allFields = []) {
  const { field_id, operator, value_field_id } = condition;
  let value = condition.value;

  // Busca o valor do campo
  let fieldValue = undefined;
  let targetFieldName = field_id;

  // Se temos a lista de campos, procura pelo field_id ou _tempId
  if (allFields.length > 0) {
    const targetField = allFields.find(f =>
      String(f.id) === String(field_id) ||
      String(f._tempId) === String(field_id)
    );

    if (targetField) {
      targetFieldName = targetField.name;
      fieldValue = formData[targetField.name];
    }
  } else {
    // Fallback: tenta usar field_id diretamente como chave do formData
    fieldValue = formData[field_id];

    // Se ainda não achou, tenta encontrar uma chave que contenha o field_id
    if (fieldValue === undefined) {
      const fieldIdStr = String(field_id);
      const possibleKey = Object.keys(formData).find(key =>
        fieldIdStr.includes(key) || key.includes('has_companion')
      );
      if (possibleKey) {
        fieldValue = formData[possibleKey];
        targetFieldName = possibleKey;
      }
    }
  }

  // Comparação campo-a-campo: o lado direito da comparação é o valor de OUTRO campo
  // (ex.: "base que participará" diferente de "base que pertence").
  if (value_field_id) {
    const cmpField = allFields.find(f =>
      String(f.id) === String(value_field_id) ||
      String(f._tempId) === String(value_field_id)
    );
    value = cmpField ? formData[cmpField.name] : undefined;
  }

  // Multi-select / checkbox group entregam fieldValue como array.
  // Cada operador trata os 2 casos (array vs string).
  const isArray = Array.isArray(fieldValue);

  switch (operator) {
    case '==':
    case 'equals':
      if (isArray) return fieldValue.some(v => String(v) === String(value));
      return fieldValue == value;

    case '!=':
    case 'not_equals':
      if (isArray) return !fieldValue.some(v => String(v) === String(value));
      return fieldValue != value;

    case 'filled_diff': {
      // Só verdadeiro quando AMBOS os lados estão preenchidos E são diferentes.
      const aEmpty = isArray ? fieldValue.length === 0 : (fieldValue === undefined || fieldValue === null || fieldValue === '');
      const vEmpty = Array.isArray(value) ? value.length === 0 : (value === undefined || value === null || value === '');
      if (aEmpty || vEmpty) return false;
      if (isArray) return !fieldValue.some(v => String(v) === String(value));
      return fieldValue != value;
    }

    case 'contains':
      if (isArray) return fieldValue.some(v => String(v).includes(value));
      return String(fieldValue || '').includes(value);

    case 'not_contains':
      if (isArray) return !fieldValue.some(v => String(v).includes(value));
      return !String(fieldValue || '').includes(value);

    case 'is_empty':
      if (isArray) return fieldValue.length === 0;
      return !fieldValue || fieldValue === '' || fieldValue === null || fieldValue === undefined;

    case 'is_not_empty':
      if (isArray) return fieldValue.length > 0;
      return !!fieldValue && fieldValue !== '';

    case '>':
    case 'greater_than':
      return Number(fieldValue) > Number(value);

    case '<':
    case 'less_than':
      return Number(fieldValue) < Number(value);

    case '>=':
    case 'greater_than_or_equal':
      return Number(fieldValue) >= Number(value);

    case '<=':
    case 'less_than_or_equal':
      return Number(fieldValue) <= Number(value);

    case 'starts_with':
      return String(fieldValue || '').startsWith(value);

    case 'ends_with':
      return String(fieldValue || '').endsWith(value);

    case 'in': {
      // value vem como array ou CSV ("opcao1, opcao2, opcao3").
      // Para multi-select, true se QUALQUER opção marcada está na lista.
      const values = Array.isArray(value) ? value.map(String) : String(value).split(',').map(v => v.trim());
      if (isArray) return fieldValue.some(v => values.includes(String(v)));
      return values.includes(String(fieldValue));
    }

    case 'not_in': {
      const notInValues = Array.isArray(value) ? value.map(String) : String(value).split(',').map(v => v.trim());
      if (isArray) return !fieldValue.some(v => notInValues.includes(String(v)));
      return !notInValues.includes(String(fieldValue));
    }

    default:
      console.warn(`Operador desconhecido: ${operator}`);
      return true; // Se operador desconhecido, mostra o campo
  }
}

/**
 * Avalia múltiplas condições com lógica AND/OR
 */
export function evaluateConditions(conditions, logic, formData, allFields = []) {
  if (!conditions || conditions.length === 0) return true;

  if (logic === 'or') {
    // Pelo menos uma condição verdadeira
    return conditions.some(cond => evaluateCondition(cond, formData, allFields));
  } else {
    // Todas as condições verdadeiras (AND)
    return conditions.every(cond => evaluateCondition(cond, formData, allFields));
  }
}

/**
 * Verifica se um campo deve ser exibido baseado em suas condições
 */
export function shouldShowField(field, formData, allFields = []) {
  if (!field) return false; // Campo null não é exibido
  if (!field.conditions || field.conditions.length === 0) {
    return true; // Sem condições = sempre exibir
  }

  // Processa condições novas (formato com rules)
  if (Array.isArray(field.conditions) && field.conditions[0]?.rules) {
    const condition = field.conditions[0];
    return evaluateConditions(condition.rules, condition.logic || 'and', formData, allFields);
  }

  // Formato antigo (compatibilidade)
  return evaluateConditions(field.conditions, field.condition_logic || 'and', formData, allFields);
}

/**
 * Verifica se um step deve ser exibido baseado em suas condições
 */
export function shouldShowStep(step, formData, allFields = []) {
  if (!step) return false; // Step null não é exibido
  if (!step.conditions || step.conditions.length === 0) {
    return true; // Sem condições = sempre exibir
  }

  // Processa condições novas (formato com rules)
  if (Array.isArray(step.conditions) && step.conditions[0]?.rules) {
    const condition = step.conditions[0];
    return evaluateConditions(condition.rules, condition.logic || 'and', formData, allFields);
  }

  // Formato antigo (compatibilidade)
  return evaluateConditions(step.conditions, step.condition_logic || 'and', formData, allFields);
}

/**
 * Filtra apenas os campos visíveis de um step baseado nas condições
 */
export function getVisibleFields(fields, formData, allFields = []) {
  return fields.filter(field => shouldShowField(field, formData, allFields));
}

/**
 * Retorna todos os campos obrigatórios visíveis de um step
 */
export function getRequiredVisibleFields(fields, formData, allFields = []) {
  return getVisibleFields(fields, formData, allFields).filter(field => field.required);
}
